import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setQuery,
  setShowModal,
  setShowModalEdit,
} from "../../store/slices/vacantesSlice.ts";
import { RootState } from "../../store/index.ts";
import { readBusinessNames } from "../../services/businessName.service.ts";
import { notification } from "../../utils/Notifications.tsx";

const initialValues = {
  type: "all",
  param: "",
  active: "all",
};

interface FilterVacantesProps {
  showAddButton?: boolean;
}

const FilterVacantes: React.FC<FilterVacantesProps> = ({ showAddButton = true }) => {
  const dispatch = useDispatch();
  const query = useSelector((state: RootState) => state.vacantes.query);
  const [filters, setFilters] = React.useState(initialValues);
  const [businessName, setBusinessName] = React.useState<{ id: number; name: string }[]>([]);
  const [timer, setTimer] = React.useState<ReturnType<typeof setTimeout> | null>(null);
  const [showPills, setShowPills] = React.useState(false);

  const buildQuery = useCallback(
    (source: typeof filters) => {
      const params = new URLSearchParams(query || "");
      const branch = params.get("branch");
      if (source.type !== "all" && source.type !== "") params.set("type", source.type);
      else params.delete("type");
      if (source.param) params.set("param", source.param);
      else params.delete("param");
      if (source.active !== "all") params.set("active", source.active);
      else params.delete("active");
      if (branch) params.set("branch", branch);
      return params.toString();
    },
    [query]
  );

  const syncFiltersFromQuery = useCallback(() => {
    const params = new URLSearchParams(query || "");
    const type = params.get("type") ?? "all";
    const param = params.get("param") ?? "";
    const active = params.get("active") ?? "all";
    setFilters({ type, param, active });
    const hasFilters = type !== "all" || param !== "" || active !== "all";
    setShowPills(hasFilters);
  }, [query]);

  useEffect(() => {
    syncFiltersFromQuery();
  }, [query]);

  const fetchDependencies = async () => {
    try {
      const bn = await readBusinessNames();
      setBusinessName(bn || []);
    } catch (error) {
      notification("Error", "Ocurrió un error al obtener los filtros", "danger");
    }
  };

  useEffect(() => {
    fetchDependencies();
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFilters((prev) => ({ ...prev, param: newValue }));
    setShowPills(newValue !== "" || filters.type !== "all" || filters.active !== "all");
    if (timer) clearTimeout(timer);
    setTimer(
      setTimeout(() => {
        const next = { ...filters, param: newValue };
        const params = new URLSearchParams(query || "");
        const branch = params.get("branch");
        if (next.type !== "all") params.set("type", next.type);
        else params.delete("type");
        if (newValue) params.set("param", newValue);
        else params.delete("param");
        if (next.active !== "all") params.set("active", next.active);
        else params.delete("active");
        if (branch) params.set("branch", branch);
        dispatch(setQuery(params.toString()));
        setTimer(null);
      }, 500)
    );
  };

  const updateFilters = () => {
    dispatch(setQuery(buildQuery(filters)));
    setShowPills(true);
  };

  const selectedFiltersList = () => {
    const list: { key: string; value: string; valueName: string; keyName: string }[] = [];
    if (filters.type !== "all" && filters.type !== "") {
      list.push({
        key: "type",
        value: filters.type,
        valueName: filters.type,
        keyName: "Razón social",
      });
    }
    if (filters.param !== "") {
      list.push({
        key: "param",
        value: filters.param,
        valueName: filters.param,
        keyName: "Búsqueda",
      });
    }
    if (filters.active !== "all") {
      list.push({
        key: "active",
        value: filters.active,
        valueName: filters.active === "true" ? "Sí" : "No",
        keyName: "Activo",
      });
    }
    if (list.length === 0) setShowPills(false);
    return list;
  };

  const removeFilter = (key: keyof typeof initialValues) => {
    const next = { ...filters, [key]: initialValues[key] };
    setFilters(next);
    const params = new URLSearchParams(query || "");
    const branch = params.get("branch");
    if (key === "type") params.delete("type");
    else if (next.type !== "all") params.set("type", next.type);
    if (key === "param") params.delete("param");
    else if (next.param) params.set("param", next.param);
    if (key === "active") params.delete("active");
    else if (next.active !== "all") params.set("active", next.active);
    if (branch) params.set("branch", branch);
    dispatch(setQuery(params.toString()));
  };

  const handleClearAll = () => {
    setFilters(initialValues);
    const params = new URLSearchParams(query || "");
    const branch = params.get("branch");
    params.delete("type");
    params.delete("param");
    params.delete("active");
    if (branch) params.set("branch", branch);
    dispatch(setQuery(params.toString()));
    setShowPills(false);
  };

  return (
    <div className="w-100">
      <div className="input-group mb-2">
        <button
          className="btn btn-white dropdown-toggle"
          type="button"
          data-bs-toggle="dropdown"
          style={{ borderRadius: "999px 0 0 999px" }}
        >
          <span className="d-none d-md-inline">Filtrar</span>
          <span className="d-inline d-md-none">
            <i className="fa fa-filter"></i>
          </span>
          <b className="caret"></b>
        </button>
        <div className="dropdown-menu p-4" onClick={(e) => e.stopPropagation()}>
          <label className="mb-0">Razón social:</label>
          <select
            className="form-select mt-1"
            id="type"
            name="type"
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            value={filters.type}
          >
            <option value="all">Todos</option>
            {businessName.map((item) => (
              <option key={item.id} value={item.name}>
                {item.name}
              </option>
            ))}
          </select>
          <label className="mt-2 mb-0">Activo:</label>
          <select
            className="form-select mt-1"
            id="active"
            name="active"
            onChange={(e) => setFilters({ ...filters, active: e.target.value })}
            value={filters.active}
          >
            <option value="all">Todos</option>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>

          <div className="row mt-3 g-2">
            <div className="col">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll();
                }}
                className="btn btn-amarillo d-block w-100 btn-lg fs-14px"
              >
                Limpiar
              </button>
            </div>
            <div className="col">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  updateFilters();
                  setShowPills(true);
                }}
                className="btn btn-azul d-block w-100 btn-lg fs-14px"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>

        <div className="flex-fill position-relative">
          <div className="input-group">
            <div
              className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
              style={{ zIndex: 10 }}
            >
              {timer ? (
                <i className="fa fa-spinner fa-spin"></i>
              ) : (
                <i className="fa fa-search opacity-5"></i>
              )}
            </div>
            <input
              type="text"
              className="form-control px-35px bg-light"
              placeholder="Ingresa un nombre..."
              id="name"
              name="name"
              onChange={handleSearchChange}
              value={filters.param}
              style={{ borderRadius: showAddButton ? "0 999px 999px 0" : "0 999px 999px 0" }}
            />
            {showAddButton && (
              <button
                className="btn btn-naranja ms-2"
                style={{ borderRadius: "999px" }}
                onClick={() => {
                  dispatch(setShowModal(true));
                  dispatch(setShowModalEdit(false));
                }}
              >
                Agregar vacante
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="mb-3 w-100">
        {showPills &&
          selectedFiltersList().map((filter) => (
            <span
              key={filter.key}
              className="badge bg-primary text-white fs-6 me-2 position-relative pe-4"
            >
              {filter.keyName}: {filter.valueName}
              <button
                type="button"
                className="btn-close btn-close-white position-absolute top-0 end-0 mt-1 me-1 ps-1"
                aria-label="Cerrar"
                onClick={() => removeFilter(filter.key as keyof typeof initialValues)}
              />
            </span>
          ))}
      </div>
    </div>
  );
};

export default FilterVacantes;
